import React, { useState } from 'react';
import { Box, Paper, Tab, Tabs, Typography } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import EmptyState from '../../components/common/EmptyState';
import FeeRequestsPanel from '../../components/finance/requests/FeeRequestsPanel';
import ManualJournalsPanel from '../../components/finance/journal/ManualJournalsPanel';
import CashCountApprovalsPanel from '../../components/finance/cashBank/CashCountApprovalsPanel';
import ConcessionApprovalsPanel from '../../components/finance/requests/ConcessionApprovalsPanel';
import { useApprovalRule } from '../../hooks/useApprovalRule';

type TabKey = 'fees' | 'concessions' | 'journals' | 'counts';

/**
 * Finance → Approvals. One inbox for everything that needs a second person:
 * waivers, write-offs, refunds and deposit returns, concessions, manual
 * journals, and cash-count differences.
 */
const ApprovalsPage: React.FC = () => {
  const { canRead } = useModulePermissions();
  const { rule } = useApprovalRule();
  const tabs: Array<{ value: TabKey; label: string }> = [
    ...(canRead('fee_waivers') || canRead('fee_refunds') || canRead('fee_writeoffs') ? [{ value: 'fees' as const, label: 'Waivers, refunds & write-offs' }] : []),
    ...(canRead('fee_concessions') || canRead('admissions_concessions_approve') ? [{ value: 'concessions' as const, label: 'Concessions' }] : []),
    ...(canRead('finance_journal') || canRead('finance_journal_approve') ? [{ value: 'journals' as const, label: 'Manual journals' }] : []),
    ...(canRead('cash_count') || canRead('finance_journal_approve') ? [{ value: 'counts' as const, label: 'Cash counts' }] : []),
  ];
  const [tab, setTab] = useState<TabKey | null>(tabs[0]?.value ?? null);
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>Approvals</Typography>
        <Typography variant="body2" color="text.secondary">
          {rule?.secondPerson === false
            ? 'The school does not require a second person, so new items post at once. Anything still waiting here can be approved by anyone who decides it — including whoever raised it.'
            : "Money actions that need a second person. Whoever raised one can't approve it."}
        </Typography>
      </Box>

      {!tab ? (
        <EmptyState icon={<LockOutlined />} title="Nothing for your role" description="Your role doesn't decide waivers, refunds, write-offs, concessions, journals or cash counts." />
      ) : (
        <>
          {tabs.length > 1 && (
            <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 1, '& .MuiTab-root': { textTransform: 'none', minHeight: 44 } }}>
                {tabs.map(t => <Tab key={t.value} value={t.value} label={t.label} />)}
              </Tabs>
            </Paper>
          )}
          {tab === 'fees' && <FeeRequestsPanel />}
          {tab === 'concessions' && <ConcessionApprovalsPanel />}
          {tab === 'journals' && <ManualJournalsPanel refreshKey={refreshKey} onDecided={() => setRefreshKey(k => k + 1)} />}
          {tab === 'counts' && <CashCountApprovalsPanel />}
        </>
      )}
    </Box>
  );
};

export default ApprovalsPage;
